import type { GameConfig } from "../config";
import { THEMES } from "../render/background";
import { makeDifficulty } from "../systems/difficulty";
import { qualifies, submitScore } from "../highscore";
import { sfx } from "../audio";
import { addCrosshair } from "../ui/crosshair";
import { addGun } from "../entities/gun";
import { addHud } from "../ui/hud";
import { createSpawner } from "../entities/spawner";
import { addBat, type Bat } from "../entities/bat";
import { view, HUD_SCALE, IS_TOUCH } from "../viewport";

const BAT_MIN = 20; // min seconds between bonus bats
const BAT_MAX = 40; // max seconds between bonus bats
const batDelay = () => rand(BAT_MIN, BAT_MAX); // random gap before the next bat
const BONUS_TIME = 10; // seconds of double score after hitting a bat
const SURVIVE_INTERVAL = 90; // seconds between survival bonuses
const SURVIVE_BONUS = 5000; // points per survival bonus

// HTML text input for high-score name entry (pops the mobile keyboard).
export const NAME_INPUT_ID = "highscore-name-input";

function createNameInput(maxLen: number, onSubmit: () => void): HTMLInputElement {
  const input = document.createElement("input");
  input.id = NAME_INPUT_ID;
  input.maxLength = maxLen;
  input.autocapitalize = "characters";
  input.autocomplete = "off";
  input.spellcheck = false;
  input.style.cssText =
    "position:fixed;left:50%;top:62%;transform:translate(-50%,-50%);" +
    "width:200px;padding:8px;font:20px monospace;text-align:center;text-transform:uppercase;" +
    "background:#000;color:#ffe9a8;border:3px solid #b81f1f;border-radius:4px;outline:none;z-index:9999;";
  input.addEventListener("keydown", (e) => {
    if (e.key === "Enter") onSubmit();
  });
  document.body.appendChild(input);
  setTimeout(() => input.focus(), 0);
  return input;
}

// Play scene: backdrop, gun/crosshair/HUD, the difficulty-driven spawner, hit
// resolution, score, and a minimal game over (full sequence is Phase 10).
export function createGameScene(config: GameConfig) {
  scene("game", () => {
    setCursor("none"); // hide the system cursor over the canvas

    // Current level (theme) index. Debug: "#game?level=N" starts in that level
    // (clamped); normal play always starts at level 0 (the exterior) and advances
    // one level after each boss (then stays on the last — "advance then stay").
    const levelM = location.hash.match(/level=(\d+)/);
    let level = levelM ? Math.min(THEMES.length - 1, Math.max(0, parseInt(levelM[1], 10))) : 0;

    // Static backdrop, drawn behind everything (swapped on a level change).
    let bgObj = add([sprite("bg" + level), pos(0, 0), z(-100)]);

    // Aim source: the mouse, or a fixed debug point via "#game?aim=x,y".
    const m = location.hash.match(/aim=(\d+),(\d+)/);
    const debugAim = m ? vec2(parseInt(m[1], 10), parseInt(m[2], 10)) : null;
    const getAim = () => debugAim ?? mousePos();

    const gun = addGun(config.weapon, getAim);

    let score = 0;
    let over = false;
    let elapsed = 0; // seconds of active play (drives the difficulty ramp)
    let overTime = 0; // seconds since game over
    let transitioning = false; // mid level-change (freezes spawns + evolution)
    const isPaused = () => over || transitioning;

    // Difficulty ramps with elapsed play time and gates the stages.
    const difficulty = makeDifficulty(config, () => elapsed);

    const nameLen = config.highscore.name_length;
    let retryPrompt: { opacity: number } | null = null;
    let entering = false; // typing a qualifying high-score name
    let nameInput: HTMLInputElement | null = null;
    let entryUI: ReturnType<typeof add>[] = []; // header text removed on submit

    // Blood fill: a red layer with a chunky, dripping pixel edge sweeps down
    // from the top, fills the screen, then fades to reveal the game-over screen.
    function startBloodFill() {
      const red = rgb(184, 31, 31);
      const dark = rgb(122, 18, 18);
      const W = width();
      const H = height();
      const CELL = 8; // pixel-edge block size
      const cols = Math.ceil(W / CELL);
      const FILL = Math.max(0.3, config.gameover.blood_fill_seconds);
      const HOLD = 0.4;
      const FADE = 0.9;
      const total = FILL + HOLD + FADE;

      // stable per-column unevenness + a few drips that run ahead of the front
      const jitter: number[] = [];
      const drip: number[] = [];
      for (let c = 0; c < cols; c++) {
        jitter[c] = Math.floor(rand(0, 3)) * CELL;
        drip[c] = rand(0, 1) < 0.16 ? 24 + Math.floor(rand(0, 5)) * CELL : 0;
      }

      const overlay = add([z(2000), fixed()]);
      let t = 0;
      let didReveal = false;
      overlay.onUpdate(() => {
        t += dt();
        if (!didReveal && t >= FILL + HOLD) {
          didReveal = true;
          reveal();
        }
        if (t >= total) destroy(overlay);
      });
      overlay.onDraw(() => {
        if (t < FILL) {
          const baseY = (t / FILL) * (H + 40) - 20;
          for (let c = 0; c < cols; c++) {
            const edge = Math.round((baseY + jitter[c] + drip[c] * (t / FILL)) / CELL) * CELL;
            if (edge <= 0) continue;
            drawRect({ pos: vec2(c * CELL, 0), width: CELL, height: Math.min(H, edge), color: red });
            if (drip[c] > 0 && edge < H) drawRect({ pos: vec2(c * CELL, edge - CELL), width: CELL, height: CELL, color: dark }); // drip tip
          }
        } else {
          const op = t < FILL + HOLD ? 1 : Math.max(0, 1 - (t - FILL - HOLD) / FADE);
          drawRect({ pos: vec2(0, 0), width: W, height: H, color: red, opacity: op });
        }
      });
    }

    function triggerGameOver() {
      if (over) return;
      over = true;
      setCursor("default");
      sfx.gameOver();
      startBloodFill(); // reveal() runs after the fill + hold, as the red fades
    }

    // Game-over screen, drawn under the blood overlay so it's revealed as the
    // red fades out.
    function reveal() {
      add([text("GAME OVER", { size: 44, font: "press2p" }), pos(center().x, center().y - 32), anchor("center"), color(184, 31, 31), z(300), fixed()]);
      if (qualifies(score, config.highscore.table_size)) {
        entering = true;
        const header = add([text("NEW HIGH SCORE!", { size: 14, font: "press2p" }), pos(center().x, center().y + 30), anchor("center"), color(127, 232, 90), z(300), fixed()]);
        const hint = add([text("TYPE NAME + ENTER", { size: 8, font: "press2p" }), pos(center().x, center().y + 86), anchor("center"), color(150, 150, 150), z(300), fixed()]);
        entryUI = [header, hint];
        nameInput = createNameInput(nameLen, submitName); // HTML input (mobile keyboard)
      } else {
        showRetryPrompt();
      }
    }

    function showRetryPrompt() {
      retryPrompt = add([text("CLICK TO RETRY", { size: 14, font: "press2p" }), pos(center().x, center().y + 40), anchor("center"), color(255, 233, 168), z(300), opacity(1), fixed()]);
    }

    function clearNameInput() {
      if (nameInput) {
        nameInput.remove();
        nameInput = null;
      }
    }

    function submitName() {
      if (!entering) return;
      void submitScore((nameInput?.value ?? "").toUpperCase(), score, config.highscore.table_size);
      entering = false;
      clearNameInput();
      entryUI.forEach((o) => destroy(o));
      entryUI = [];
      showRetryPrompt();
    }

    onUpdate(() => {
      if (!over) {
        // Freeze game evolution during the boss fight: the difficulty ramp, the
        // survival-bonus countdown and bat timing all key off `elapsed`, so the
        // boss fight is a self-contained interlude and progression resumes from
        // exactly where it paused once the boss is defeated.
        if (!spawner.bossActive() && !transitioning) elapsed += dt();
        if (bonusTime > 0) bonusTime = Math.max(0, bonusTime - dt());
        if (elapsed >= nextBat) {
          bats.push(addBat({ fromLeft: batFromLeft, isPaused }));
          batFromLeft = !batFromLeft;
          nextBat += batDelay();
        }
        if (elapsed >= nextSurvive) {
          score += SURVIVE_BONUS; // flat survival reward (not doubled)
          showSurvive(SURVIVE_BONUS);
          sfx.bonus();
          nextSurvive += SURVIVE_INTERVAL;
        }
        for (let i = bats.length - 1; i >= 0; i--) {
          if (!bats[i].isAlive()) bats.splice(i, 1);
        }
        return;
      }
      overTime += dt();
      // blink the retry prompt once it's shown (after the blood reveal)
      if (retryPrompt) {
        retryPrompt.opacity = Math.floor(overTime / 0.5) % 2 === 0 ? 1 : 0;
      }
      // auto-return to the home screen after highscore_delay seconds.
      if (overTime >= config.gameover.highscore_delay) {
        clearNameInput();
        go("menu");
      }
    });

    // Zombie spawner. Window kills score window_kill; in stage 2 a crawl-expired
    // zombie falls out and walks (headshot-only); reaching the player ends it.
    const spawner = createSpawner({
      slots: THEMES[level].slots,
      baseY: THEMES[level].groundY - 1, // tuned to sit on the ground-level art
      pelletRadius: config.weapon.pellet_radius,
      difficulty,
      isPaused,
      windowKillScore: config.scoring.window_kill,
      headshotScore: config.scoring.headshot,
      bossTuning: config.boss,
      onReachPlayer: triggerGameOver,
      onLunge: () => sfx.growl(),
      onBossRoar: () => sfx.bossRoar(),
      onAxeThrow: () => sfx.axeThrow(),
      onAxeShatter: () => sfx.axeBreak(),
      onBossDefeated: (bossLvl) => {
        score += config.boss.kill_score * bossLvl; // flat milestone reward
        showSurvive(config.boss.kill_score * bossLvl, "BOSS DOWN!");
        sfx.bonus();
        // Advance to the next level after the boss (then stay on the last).
        if (level < THEMES.length - 1) {
          transitioning = true; // freeze spawns/evolution while the banner shows + fades
          wait(1.6, startLevelTransition);
        }
      },
    });

    // Level change: fade to black, swap the backdrop + spawner geometry, fade in.
    function startLevelTransition() {
      transitioning = true;
      const FADE = 0.7;
      const HOLD = 0.25;
      const overlay = add([z(1900), fixed()]);
      let t = 0;
      let swapped = false;
      overlay.onUpdate(() => {
        t += dt();
        if (!swapped && t >= FADE) {
          swapped = true;
          level++;
          const nt = THEMES[level];
          destroy(bgObj);
          bgObj = add([sprite("bg" + level), pos(0, 0), z(-100)]);
          spawner.setTheme(nt.slots, nt.groundY - 1);
          destroyAll("hole"); // clear bullet holes from the old level
        }
        if (t >= FADE + HOLD + FADE) {
          transitioning = false;
          destroy(overlay);
        }
      });
      overlay.onDraw(() => {
        const op = t < FADE ? t / FADE : t < FADE + HOLD ? 1 : Math.max(0, 1 - (t - FADE - HOLD) / FADE);
        drawRect({ pos: vec2(0, 0), width: width(), height: height(), color: rgb(0, 0, 0), opacity: op });
        if (t >= FADE - 0.1 && t < FADE + HOLD + 0.2) {
          drawText({ text: `LEVEL ${level + 1}`, size: 28, font: "press2p", pos: vec2(center().x, center().y), anchor: "center", color: rgb(184, 31, 31) });
        }
      });
    }

    // Floating popup text that rises and fades at a point.
    function showPopup(msg: string, p: ReturnType<typeof getAim>, col: [number, number, number]) {
      const label = add([text(msg, { size: 18, font: "press2p" }), pos(p.x, p.y - 22), anchor("center"), color(...col), opacity(1), z(1100)]);
      const max = 0.9;
      let life = max;
      label.onUpdate(() => {
        life -= dt();
        label.pos.y -= 34 * dt();
        label.opacity = Math.max(0, life / max);
        if (life <= 0) destroy(label);
      });
    }

    // Prominent banner: green screen flash + centered panel. Reused for both the
    // survival bonus ("SURVIVED!") and the boss-defeat reward ("BOSS DOWN!").
    function showSurvive(pts: number, title = "SURVIVED!") {
      const flash = add([rect(width(), height()), pos(0, 0), color(127, 232, 90), opacity(0.3), z(1080), fixed()]);
      let fl = 0.4;
      flash.onUpdate(() => {
        fl -= dt();
        flash.opacity = Math.max(0, 0.3 * (fl / 0.4));
        if (fl <= 0) destroy(flash);
      });

      const cy = center().y - 50;
      const panel = add([rect(330, 74, { radius: 6 }), pos(center().x, cy), anchor("center"), color(0, 0, 0), outline(3, rgb(127, 232, 90)), opacity(0.9), z(1090), fixed()]);
      const big = add([text(title, { size: 22, font: "press2p" }), pos(center().x, cy - 13), anchor("center"), color(127, 232, 90), opacity(1), z(1100), fixed()]);
      const sub = add([text(`+${pts}`, { size: 20, font: "press2p" }), pos(center().x, cy + 16), anchor("center"), color(255, 233, 168), opacity(1), z(1100), fixed()]);
      let life = 1.9;
      const ctl = add([]);
      ctl.onUpdate(() => {
        life -= dt();
        const op = life < 0.6 ? Math.max(0, life / 0.6) : 1;
        panel.opacity = 0.9 * op;
        big.opacity = op;
        sub.opacity = op;
        if (life <= 0) {
          destroy(panel);
          destroy(big);
          destroy(sub);
          destroy(ctl);
        }
      });
    }

    // Bonus bats: one every 20-40s (random); hitting one doubles score for a while.
    const bats: Bat[] = [];
    let bonusTime = 0; // seconds of double score remaining
    let nextBat = batDelay();
    let batFromLeft = true;
    let nextSurvive = SURVIVE_INTERVAL;
    const addScore = (pts: number) => (score += bonusTime > 0 ? pts * 2 : pts);

    // Boss fight: a running kill total triggers a (recurring, escalating) boss.
    let totalKills = 0;
    let nextBossKills = config.boss.first_kills;

    if (!IS_TOUCH) addCrosshair(getAim); // no mouse crosshair on touch devices
    addHud(gun, () => elapsed);

    // Score + bonus, top-left of the visible region (re-anchored each frame so
    // they survive the mobile side-crop).
    const scoreLabel = add([text("0", { size: 14 * HUD_SCALE, font: "press2p" }), pos(8, 8), color(255, 233, 168), z(120), fixed()]);
    scoreLabel.onUpdate(() => {
      scoreLabel.text = `${score}`;
      scoreLabel.pos = vec2(view.left + 8, view.top + 8);
    });

    const bonusLabel = add([text("", { size: 12 * HUD_SCALE, font: "press2p" }), pos(8, 26), color(127, 232, 90), z(120), fixed()]);
    bonusLabel.onUpdate(() => {
      bonusLabel.text = bonusTime > 0 ? `x2  ${Math.ceil(bonusTime)}s` : "";
      bonusLabel.pos = vec2(view.left + 8, view.top + 18 * HUD_SCALE);
    });

    // Boss energy bar (top-center): visible only while a boss exists.
    const bossBar = add([z(120), fixed()]);
    bossBar.onDraw(() => {
      const frac = spawner.bossHpFraction();
      if (frac < 0) return;
      const w = 300 * HUD_SCALE;
      const h = 12 * HUD_SCALE;
      const x = center().x - w / 2;
      const y = view.top + 34; // below the score/bonus row so they don't overlap
      drawRect({ pos: vec2(x - 2, y - 2), width: w + 4, height: h + 4, color: rgb(20, 8, 12), outline: { color: rgb(184, 31, 31), width: 2 * HUD_SCALE } });
      drawRect({ pos: vec2(x, y), width: w, height: h, color: rgb(40, 16, 16) });
      drawRect({ pos: vec2(x, y), width: w * Math.max(0, frac), height: h, color: rgb(200, 36, 36) });
      drawRect({ pos: vec2(x, y), width: w * Math.max(0, frac), height: 3 * HUD_SCALE, color: rgb(240, 96, 96) }); // sheen
    });

    // Flickering wall candles for levels that define them (e.g. the hall). Drawn
    // behind the zombies (z below 50), reading the current level's candle list.
    const candleLayer = add([z(-50)]);
    candleLayer.onDraw(() => {
      const cs = THEMES[level].candles;
      if (!cs) return;
      for (let i = 0; i < cs.length; i++) {
        const c = cs[i];
        const tt = time() * 3.5 + i * 1.9; // 50% slower flicker
        const fl = 0.8 + 0.12 * Math.sin(tt) + 0.07 * Math.sin(tt * 2.7 + 1.3); // ~0.6–1.0 flicker
        drawCircle({ pos: vec2(c.x, c.y - 16), radius: 30 * fl, color: rgb(255, 170, 70), opacity: 0.2 * fl });
        drawCircle({ pos: vec2(c.x, c.y - 16), radius: 15 * fl, color: rgb(255, 210, 120), opacity: 0.28 * fl });
        drawRect({ pos: vec2(c.x - 4, c.y - 16), width: 8, height: 16, color: rgb(238, 228, 196) }); // candle
        drawRect({ pos: vec2(c.x - 5, c.y), width: 10, height: 3, color: rgb(60, 40, 20) }); // holder
        drawCircle({ pos: vec2(c.x, c.y - 18), radius: 3.4 * fl, color: rgb(255, 140, 40) }); // flame
        drawCircle({ pos: vec2(c.x, c.y - 19), radius: 2 * fl, color: rgb(255, 225, 120) }); // flame core
      }
    });

    // Left click = fire (resolves walkers then window zombies). After game over,
    // a click retries (once the freeze elapses). Right click = reload.
    // A permanent pellet mark at the aim point (stays for the rest of the game).
    function addBulletHole(p: ReturnType<typeof getAim>) {
      const hole = add([pos(p.x, p.y), z(40), "hole"]); // tagged so a level change can clear them
      hole.onDraw(() => {
        drawCircle({ radius: 5, color: rgb(150, 128, 96), opacity: 0.55 }); // scuff ring
        drawCircle({ radius: 3, color: rgb(10, 8, 14) }); // dark hole
      });
    }

    // Fire a shot aimed at `aim` (mouse position on desktop, tap point on touch).
    function fireAt(aim: ReturnType<typeof getAim>) {
      const shot = gun.tryFire();
      if (shot === "empty") sfx.dryFire();
      if (shot !== "fired") return;
      if (gun.shells() === 0) sfx.shootLast(); // just fired the last shell
      else sfx.shoot();
      // bats first: hitting one grants the double-score bonus
      let batHit = false;
      for (const b of bats) {
        if (b.isAlive() && b.contains(aim, config.weapon.pellet_radius)) {
          b.hit();
          batHit = true;
        }
      }
      if (batHit) {
        bonusTime = BONUS_TIME;
        sfx.bonus();
        showPopup("DOUBLE SCORE!", aim, [127, 232, 90]);
      }
      const res = spawner.resolveHit(aim);
      if (res.kills > 0) sfx.splat();
      let pts = res.points;
      if (res.kills >= 2) {
        pts *= 2; // multikill: double points
        showPopup("MULTIKILL!", aim, [232, 224, 42]);
      }
      addScore(pts);
      // running kill total drives the boss milestone (skip while one is active)
      totalKills += res.kills;
      if (totalKills >= nextBossKills && !spawner.bossActive()) {
        spawner.triggerBoss();
        nextBossKills += config.boss.kill_interval;
      }
      if (!res.hit && !batHit) addBulletHole(aim); // missed: mark the spot
    }

    function reload() {
      if (over) return;
      const before = gun.shells(); // shells before the reload starts
      if (gun.startReload()) sfx.reload((gun.magazineSize() - before) / gun.magazineSize());
    }

    // --- desktop: mouse ---
    onMousePress("left", () => {
      if (over) {
        if (retryPrompt && !entering) go("game"); // retry once revealed
      } else {
        fireAt(getAim());
      }
    });
    onMousePress("right", reload);

    // --- mobile: tap to shoot, two-finger tap to reload ---
    const touches = new Set<number>();
    let gestureMulti = false; // a second finger joined this gesture
    let tapPos = getAim();
    onTouchStart((p, t) => {
      touches.add(t.identifier);
      tapPos = p;
      if (touches.size >= 2) {
        gestureMulti = true;
        reload(); // two fingers = reload
      }
    });
    onTouchEnd((_p, t) => {
      touches.delete(t.identifier);
      if (touches.size > 0) return; // wait until all fingers lift
      if (over) {
        if (retryPrompt && !entering) go("game");
      } else if (!gestureMulti) {
        fireAt(tapPos); // single tap = fire at the tap point
      }
      gestureMulti = false;
    });

    // Debug: "#game?score=N" presets the score (to test high-score entry).
    const scoreM = location.hash.match(/score=(\d+)/);
    if (scoreM) score = parseInt(scoreM[1], 10);

    // Debug: "#game?zoms" fills every window with a (persistent) zombie.
    if (/zoms/.test(location.hash)) spawner.debugFill();

    // Debug: "#game?bat" spawns a bonus bat immediately.
    if (/bat/.test(location.hash)) bats.push(addBat({ fromLeft: true, isPaused }));

    // Debug: "#game?boss" triggers the boss sequence immediately.
    if (/boss/.test(location.hash)) wait(0.2, () => spawner.debugBoss());

    // Debug: "#game?slots" outlines every spawn slot (to align art ↔ slots).
    if (/slots/.test(location.hash)) {
      const dbg = add([z(115), fixed()]);
      dbg.onDraw(() => {
        for (const s of THEMES[level].slots) {
          drawRect({ pos: vec2(s.x, s.y), width: s.w, height: s.h, fill: false, outline: { color: s.kind === "door" ? rgb(80, 200, 255) : rgb(127, 232, 90), width: 2 } });
        }
      });
    }

    // Debug: "#game?entry" reveals the game-over screen now (use with score=N).
    if (/entry/.test(location.hash)) {
      over = true;
      reveal();
    }

    // Debug: "#game?survive" shows the survival-bonus banner.
    if (/survive/.test(location.hash)) wait(0.3, () => showSurvive(SURVIVE_BONUS));

    // Debug: "#game?walk" spawns a walker; "#game?walk=0.85" spawns it advanced.
    const walkM = location.hash.match(/walk(?:=([\d.]+))?/);
    if (walkM) spawner.debugWalker(walkM[1] ? parseFloat(walkM[1]) : 0);

    // Debug: "#game?pair" spawns a near + far walker to check z-ordering.
    if (/pair/.test(location.hash)) {
      spawner.debugWalker(0.32);
      spawner.debugWalker(0.78);
    }

    // Debug: "#game?stack" spawns two overlapping walkers (to test multikill).
    if (/stack/.test(location.hash)) {
      spawner.debugWalker(0.55);
      spawner.debugWalker(0.55);
    }

    // Debug: "#game?fall" plays the fall animation; "#game?fall=0.6" freezes it.
    const fallM = location.hash.match(/fall(?:=([\d.]+))?/);
    if (fallM) spawner.debugFall(fallM[1] ? parseFloat(fallM[1]) : undefined);

    // Debug: "#game?shoot" fires once at the aim point (use with aim=x,y).
    if (/shoot/.test(location.hash)) {
      wait(0.15, () => {
        if (gun.tryFire() !== "fired") return;
        const res = spawner.resolveHit(getAim());
        addScore(res.kills >= 2 ? res.points * 2 : res.points);
        if (res.kills >= 2) showPopup("MULTIKILL!", getAim(), [232, 224, 42]);
        if (!res.hit) addBulletHole(getAim());
      });
    }

    // Debug: "#game?demo" auto-fires and reloads so the cycle can be observed.
    if (/demo/.test(location.hash)) {
      loop(0.45, () => (gun.shells() > 0 ? gun.tryFire() : gun.startReload()));
    }
    // Debug: "#game?rl" empties the mag and reloads immediately (shows the bar).
    if (/\brl\b/.test(location.hash)) {
      while (gun.shells() > 0) gun.tryFire();
      gun.startReload();
    }

    // Esc returns to the title. A document-level listener (rather than KAPLAY's
    // canvas-focused onKeyPress) keeps it working while the high-score name <input>
    // holds keyboard focus after game over. Removed when the scene unloads.
    const onEscKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        clearNameInput();
        go("menu");
      }
    };
    document.addEventListener("keydown", onEscKey);
    add([]).onDestroy(() => document.removeEventListener("keydown", onEscKey));
  });
}
